import React, { useState, useMemo, useEffect } from 'react';
import { GlobalButton } from '../GlobalButton';
import { GlobalTextarea } from '../GlobalTextarea';
import { GlobalInput } from '../GlobalInput';
import { GlobalDropdown } from '../GlobalDropdown';
import GlobalTabs from '../GlobalTabs';
import SmallServiceCard from '../SmallServiceCard';
import { SOPCard } from './data/SOPCard';
import styles from './SOPsTab.module.css';
import { useLocale } from '../../context/DynamicLocaleProvider';
import { useToast } from '../../context/ToastProvider';
import { useTheme } from '../../context/ThemeProvider';
import { FilterBar } from '../FilterBar';
import { StandardOperatingProcedure } from '../../types';
import { createSOP } from '../../services/domainEntities';
import GlobalEmptyState from '../GlobalEmptyState';
import GlobalSidebar from '../GlobalSidebar';
import { URSAutoComplete } from '../URSAutoComplete';
import { FSAutoComplete } from '../FSAutoComplete';
import { RiskAutoComplete } from '../RiskAutoComplete';
import { TestAutoComplete } from '../TestAutoComplete';
import TransformEntitiesSidebar from '../TransformEntitiesSidebar';
import EntityGenerationSidebar from '../EntityGenerationSidebar';
import GenerateEntitySidebar from '../GenerateEntitySidebar';
import AddDocumentSidebar from '../AddDocumentSidebar';
import { collection, getDocs, doc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useUser } from '../../context/UserContext';
import DoubleConfirmationModal from '../DoubleConfirmationModal';
import { exportToCSV } from '../../utils/exportToCSV';

interface SOPsTabProps {
  projectId: string;
  onExport?: () => void;
  onNavigateToEntity?: (entityId: string, entityType: 'URS' | 'FS' | 'TEST' | 'RISK' | 'SOP') => void;
}

export const SOPsTab: React.FC<SOPsTabProps> = ({ projectId, onExport, onNavigateToEntity }) => {
  const { t } = useLocale();
  const showToast = useToast();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const { user } = useUser();
  
  const [sops, setSops] = useState<StandardOperatingProcedure[]>([]);
  const [selectedSOP, setSelectedSOP] = useState<StandardOperatingProcedure | null>(null);
  const [showSOPEditor, setShowSOPEditor] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sopToDelete, setSopToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showTransformEntitiesSidebar, setShowTransformEntitiesSidebar] = useState(false);
  const [showEntityGenerationSidebar, setShowEntityGenerationSidebar] = useState(false);
  const [showGenerateEntitySidebar, setShowGenerateEntitySidebar] = useState(false);
  const [selectedEntitiesForGeneration, setSelectedEntitiesForGeneration] = useState<Array<{
    id: string;
    type: 'URS' | 'FS' | 'TEST' | 'RISK' | 'SOP';
    title: string;
  }>>([]);
  const [showAddDocumentSidebar, setShowAddDocumentSidebar] = useState(false);

  useEffect(() => {
    const loadSOPs = async () => {
      if (!projectId) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const col = collection(db, 'projects', projectId, 'sops');
        const snapshot = await getDocs(col);
        const loaded: StandardOperatingProcedure[] = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            ...data,
            id: data.id || `SOP-${doc.id.substring(0, 8)}`,
            _firestoreId: doc.id
          } as StandardOperatingProcedure & { _firestoreId: string };
        });
        setSops(loaded);
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error loading SOPs:', err);
        }
        showToast(t('project_tab_sop_load_failed') || 'Failed to load SOPs', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadSOPs();
  }, [projectId, showToast, t]);

  const handleSaveSOP = async (sop: StandardOperatingProcedure) => {
    try {

      const firestoreId = (sop as any)?._firestoreId;
      
      if (firestoreId) {
        // Update existing
        const docRef = doc(db, 'projects', projectId, 'sops', firestoreId);
        const { _firestoreId, ...dataToSave } = sop as any;
        await updateDoc(docRef, {
          ...dataToSave,
          updatedAt: serverTimestamp()
        } as any);

      } else {
        // Create new
        await createSOP(projectId, sop);
      }
      
      // Reload SOPs from Firestore
      const col = collection(db, 'projects', projectId, 'sops');
      const snapshot = await getDocs(col);
      const loaded: StandardOperatingProcedure[] = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          ...data,
          id: data.id || `SOP-${doc.id.substring(0, 8)}`,
          _firestoreId: doc.id
        } as StandardOperatingProcedure & { _firestoreId: string };
      });
      setSops(loaded);
      
      setShowSOPEditor(false);
      setSelectedSOP(null);
      showToast(t('project_tab_sop_saved') || 'SOP saved', 'success');
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error(err);
      }
      showToast(t('project_tab_sop_save_failed') || 'Failed to save SOP', 'error');
    }
  };

  const handleCreateSOP = () => {
    const newSOP: StandardOperatingProcedure = {
      id: `SOP-${String(sops.length + 1).padStart(3, '0')}`,
      type: 'SOP',
      title: '',
      code: `SOP-${String(sops.length + 1).padStart(3, '0')}`,
      version: '1.0',
      effective_date: new Date().toISOString().substring(0, 10),
      description: '',
      gxp_relevance: 'High',
      status: 'Draft',
      links: { urs_ids: [], fs_ids: [], risk_ids: [], test_ids: [] },
      meta: { department: '' },
      createdAt: new Date(),
      updatedAt: new Date()
    };
    setSelectedSOP(newSOP);
    setShowSOPEditor(true);
  };

  const extractSOPNumber = (id: string): number => {
    const match = id.match(/(\d+)$/);
    return match ? parseInt(match[1], 10) : 0;
  };

  const handleDeleteSOP = (sopId: string) => {
    setSopToDelete(sopId);
    setShowDeleteModal(true);
  };

  const confirmDeleteSOP = async () => {
    if (!sopToDelete) return;
    setIsDeleting(true);
    try {
      const sop = sops.find(s => s.id === sopToDelete);
      if (sop && (sop as any)._firestoreId) {
        const docRef = doc(db, 'projects', projectId, 'sops', (sop as any)._firestoreId);
        await deleteDoc(docRef);
      }
      setSops(sops.filter(s => s.id !== sopToDelete));
      showToast(t('project_tab_sop_deleted') || 'SOP deleted', 'success');
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error(err);
      }
      showToast(t('project_tab_sop_delete_failed') || 'Failed to delete SOP', 'error');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setSopToDelete(null);
    }
  };

  const handleEntityClick = (entityId: string, entityType: 'URS' | 'FS' | 'TEST' | 'RISK') => {
    onNavigateToEntity?.(entityId, entityType);
  };

  const handleAiGenerate = () => {
    // Open the Generate Entity sidebar
    setShowGenerateEntitySidebar(true);
  };

  const handleAiGenerateFromSOP = () => {
    // Open sidebar with ALL SOPs selected
    if (sops.length === 0) {
      showToast(t('project_tab_no_sops_to_generate') || 'No SOPs available. Please create a SOP first.', 'error');
      return;
    }
    const allSOPs = sops.map(sop => ({
      id: sop.id,
      type: 'SOP' as const,
      title: sop.title
    }));
    setSelectedEntitiesForGeneration(allSOPs);
    setShowEntityGenerationSidebar(true);
  };

  const handleUploadSOPClick = () => {
    setShowAddDocumentSidebar(true);
  };

  // Filter and sort SOPs based on search and status
  const filteredSOPs = useMemo(() => {
    const filtered = sops.filter(sop => {
      const matchesSearch = !searchValue || 
        sop.id.toLowerCase().includes(searchValue.toLowerCase()) ||
        sop.title.toLowerCase().includes(searchValue.toLowerCase()) ||
        sop.description.toLowerCase().includes(searchValue.toLowerCase());
      const matchesStatus = !statusFilter || sop.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
    
    return filtered.sort((a, b) => {
      const numA = extractSOPNumber(a.id);
      const numB = extractSOPNumber(b.id);
      return numA - numB;
    });
  }, [sops, searchValue, statusFilter]);

  const tabs = [
    {
      label: t('project_tab_sops') || 'SOPs',
      queryValue: 'sops',
      content: (
        <>
          {/* FilterBar */}
          <FilterBar
            filters={[
              {
                id: 'status',
                label: t('project_tab_status') || 'Status',
                options: [
                  { value: '', label: t('project_tab_all') || 'All' },
                  { value: 'Draft', label: t('project_tab_status_draft') || 'Draft' },
                  { value: 'In Review', label: t('project_tab_status_in_review') || 'In Review' },
                  { value: 'Effective', label: t('project_tab_status_approved') || 'Effective' },
                  { value: 'Obsolete', label: t('project_tab_status_rejected') || 'Obsolete' }
                ],
                value: statusFilter,
                onChange: (value) => setStatusFilter(value)
              }
            ]}
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            searchPlaceholder={t('project_tab_search_sops') || 'Search SOPs...'}
            showInviteButton={true}
            onAddUser={handleCreateSOP}
            addButtonText={t('project_tab_create_sop') || 'Create SOP'}
          />

          {loading ? null : filteredSOPs.length === 0 ? (
            <GlobalEmptyState
              buttonText={t('project_tab_create_sop') || 'Create SOP'}
              buttonIcon={isDarkMode ? "/assets/icons/plus.circle.svg" : "/assets/icons/plus.circle.black.svg"}
              onButtonClick={handleCreateSOP}
            />
          ) : (
            <div className={styles.sopsList}>
              {filteredSOPs.map((sop) => (
                <div key={sop.id} className={styles.sopItem}>
          <SOPCard
            sop={sop}
            projectId={projectId}
            onView={(sop) => {
              setSelectedSOP(sop);
              setShowSOPEditor(true);
            }}
            onEdit={(sop) => {
              setSelectedSOP(sop);
              setShowSOPEditor(true);
            }}
            onDelete={handleDeleteSOP}
            onViewVersions={(sop) => {
              showToast(t('project_tab_viewing_versions') || 'Viewing versions', 'info');
            }}
            onEntityClick={handleEntityClick}
          />
                </div>
              ))}
            </div>
          )}
        </>
      )
    },
    {
      label: t('project_tab_options') || 'Options',
      queryValue: 'options',
      content: (
        <div className={styles.optionsGrid}>

          {/* Create new SOP */}
          <SmallServiceCard
            id="create-sop"
            title={t('project_tab_create_new_sop') || 'Create New SOP'}
            description={t('project_tab_create_new_sop_desc') || 'Create a new Standard Operating Procedure from scratch'}
            fileTypeIcon=""
            serviceIcon=""
            buttonIcon={isDarkMode ? "/assets/icons/plus.circle.svg" : "/assets/icons/plus.circle.black.svg"}
            buttonText={t('project_tab_create') || 'Create'}
            buttonOnClick={handleCreateSOP}
          />

          {/* Import template */}
          <SmallServiceCard
            id="import-template"
            title={t('project_tab_read_file_with_ai') || 'Datei mit KI einlesen'}
            description={t('project_tab_read_file_with_ai_sop_desc') || 'Importieren Sie Dateien um SOPs automatisch auslesen und erstellen zu lassen.'}
            fileTypeIcon={isDarkMode ? "/assets/icons/apple.intelligence.svg" : "/assets/icons/apple.intelligence.black.svg"}
            serviceIcon=""
            buttonIcon={isDarkMode ? "/assets/icons/icloud.and.arrow.up.svg" : "/assets/icons/icloud.and.arrow.up.black.svg"}
            buttonText={t('project_tab_import') || 'Import'}
            buttonOnClick={handleUploadSOPClick}
            disabled={!user}
          />

          {/* AI Draft */}
          <SmallServiceCard
            id="ai-draft"
            title={t('project_tab_ai_draft') || 'AI Draft'}
            description={t('project_tab_ai_draft_desc') || 'Generate an SOP draft using AI assistance'}
            fileTypeIcon={isDarkMode ? "/assets/icons/apple.intelligence.svg" : "/assets/icons/apple.intelligence.black.svg"}
            serviceIcon=""
            buttonIcon={isDarkMode ? "/assets/icons/arrow.right.circle.svg" :"/assets/icons/arrow.right.circle.black.svg"}
            buttonText={t('project_tab_generate') || 'Generate'}
            buttonOnClick={handleAiGenerate}
          />

          {/* Generate with AI from ALL SOPs */}
          <SmallServiceCard
            id="ai-generate-from-all-sops"
            title={t('project_tab_ai_generate_from_all_sops') || 'Mit KI aus SOP generieren'}
            description={t('project_tab_ai_generate_from_all_sops_desc') || 'Generieren Sie verbundene Entitäten (URS, Tests, Risiken, SOPs) mit KI wie aus den FS\'s'}
            fileTypeIcon={isDarkMode ? "/assets/icons/apple.intelligence.svg" : "/assets/icons/apple.intelligence.black.svg"}
            serviceIcon=""
            buttonIcon={isDarkMode ? "/assets/icons/arrow.right.circle.svg" :"/assets/icons/arrow.right.circle.black.svg"}
            buttonText={t('project_tab_generate') || 'Generieren'}
            buttonOnClick={handleAiGenerateFromSOP}
          />

          {/* Transform Entities */}
          <SmallServiceCard
            id="transform-entities-sops"
            title={t('project_tab_transform_sops') || 'Transform Work Instruction'}
            description={t('project_tab_transform_sops_desc') || 'Transform work instructions to replace content, translate, or rephrase.'}
            fileTypeIcon={isDarkMode ? "/assets/icons/apple.intelligence.svg" : "/assets/icons/apple.intelligence.black.svg"}
            serviceIcon=""
            buttonIcon={isDarkMode ? "/assets/icons/arrow.right.circle.svg" :"/assets/icons/arrow.right.circle.black.svg"}
            buttonText={t('project_tab_transform') || 'Transform'}
            buttonOnClick={() => setShowTransformEntitiesSidebar(true)}
          />

          {/* Export PDF */}
          <SmallServiceCard
            id="export-pdf-sops"
            title={t('project_tab_sops_export_pdf') || 'Als PDF exportieren'}
            description={t('project_tab_sops_export_pdf_desc') || 'Exportieren Sie alle SOPs gesammelt als ein PDF Dokument.'}
            fileTypeIcon="/assets/graphics/pdf-icon.svg"
            serviceIcon=""
            buttonIcon={isDarkMode ? "/assets/icons/arrow.down.circle.white.svg" : "/assets/icons/arrow.down.circle.black.svg"}
            buttonText={t('project_tab_export') || 'Exportieren'}
            buttonOnClick={onExport || (() => showToast(t('project_tab_exporting') || 'Exporting...', 'info'))}
            disabled
          />

          {/* Export CSV */}
          <SmallServiceCard
            id="export-csv-sops"
            title={t('project_tab_sops_export_csv') || 'Als CSV exportieren'}
            description={t('project_tab_sops_export_csv_desc') || 'Exportieren Sie alle SOPs gesammelt als ein CSV Dokument.'}
            fileTypeIcon={"/assets/graphics/csv_icon.svg"}
            serviceIcon=""
            buttonIcon={isDarkMode ? "/assets/icons/arrow.down.circle.white.svg" : "/assets/icons/arrow.down.circle.black.svg"}
            buttonText={t('project_tab_export') || 'Exportieren'}
            buttonOnClick={() => {
              try {
                if (sops.length === 0) {
                  showToast(t('project_tab_no_sops_to_export') || 'No SOPs to export', 'warning');
                  return;
                }
                exportToCSV(sops, `SOPs_Export_${projectId}_${new Date().toISOString().split('T')[0]}`);
                showToast(t('project_tab_export_success') || 'Export successful', 'success');
              } catch (error) {
                console.error('Error exporting CSV:', error);
                showToast(t('project_tab_export_failed') || 'Export failed', 'error');
              }
            }}
          />

        </div>
      )
    }
  ];

  return (
    <div className={styles.sopsTab}>
      <div className={styles.tabHeader}>
        <h2 className={styles.tabTitle}>
          {t('project_page_tab_sops') || 'SOPs (Standard Operating Processes)'}
        </h2>
      </div>

      <GlobalTabs tabs={tabs} queryParam="tab" defaultTabValue="sops" />

      {/* Generate Entity Sidebar */}
      <GenerateEntitySidebar
        isOpen={showGenerateEntitySidebar}
        onClose={() => {
          setShowGenerateEntitySidebar(false);
        }}
        projectId={projectId}
        entityType="SOP"
        onSuccess={() => {
          // Reload SOPs to show generated entity
          const loadSOPs = async () => {
            try {
              const col = collection(db, 'projects', projectId, 'sops');
              const snapshot = await getDocs(col);
              const loaded: StandardOperatingProcedure[] = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                  ...data,
                  id: data.id || `SOP-${doc.id.substring(0, 8)}`,
                  _firestoreId: doc.id
                } as StandardOperatingProcedure & { _firestoreId: string };
              });
              setSops(loaded);
            } catch (err) {
              if (process.env.NODE_ENV !== 'production') {
                console.error('Error reloading SOPs:', err);
              }
            }
          };
          loadSOPs();
        }}
      />

      {/* Entity Generation Sidebar */}
      <EntityGenerationSidebar
        isOpen={showEntityGenerationSidebar}
        onClose={() => {
          setShowEntityGenerationSidebar(false);
          setSelectedEntitiesForGeneration([]);
        }}
        projectId={projectId}
        selectedEntities={selectedEntitiesForGeneration}
        onSuccess={() => {
          // Reload SOPs to show any new entities
          const loadSOPs = async () => {
            try {
              const col = collection(db, 'projects', projectId, 'sops');
              const snapshot = await getDocs(col);
              const loaded: StandardOperatingProcedure[] = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                  ...data,
                  id: data.id || `SOP-${doc.id.substring(0, 8)}`,
                  _firestoreId: doc.id
                } as StandardOperatingProcedure & { _firestoreId: string };
              });
              setSops(loaded);
            } catch (err) {
              if (process.env.NODE_ENV !== 'production') {
                console.error('Error reloading SOPs:', err);
              }
            }
          };
          loadSOPs();
        }}
      />

      {/* Transform Entities Sidebar */}
      <TransformEntitiesSidebar
        isOpen={showTransformEntitiesSidebar}
        onClose={() => {
          setShowTransformEntitiesSidebar(false);
        }}
        projectId={projectId}
        onSuccess={() => {
          // Reload SOPs to show transformed entities
          const loadSOPs = async () => {
            try {
              const col = collection(db, 'projects', projectId, 'sops');
              const snapshot = await getDocs(col);
              const loaded: StandardOperatingProcedure[] = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                  ...data,
                  id: data.id || `SOP-${doc.id.substring(0, 8)}`,
                  _firestoreId: doc.id
                } as StandardOperatingProcedure & { _firestoreId: string };
              });
              setSops(loaded);
            } catch (err) {
              if (process.env.NODE_ENV !== 'production') {
                console.error('Error reloading SOPs:', err);
              }
            }
          };
          loadSOPs();
        }}
      />

      <DoubleConfirmationModal
        isOpen={showDeleteModal}
        title={t('project_tab_delete_sop_title')?.replace('{id}', sopToDelete || '') || `Delete SOP ${sopToDelete}?`}
        paragraph={t('project_tab_delete_sop_paragraph') || 'Are you sure you want to delete this SOP? This action cannot be undone.'}
        primaryLabel={t('global_delete') || 'Delete'}
        primaryIcon="/assets/icons/trash.red.svg"
        secondaryLabel={t('global_cancel') || 'Cancel'}
        secondaryIcon="/assets/icons/xmark.svg"
        onPrimary={confirmDeleteSOP}
        onSecondary={() => {
          setShowDeleteModal(false);
          setSopToDelete(null);
        }}
        primaryDestructive
        loading={isDeleting}
      />

      {showSOPEditor && selectedSOP && (
        <SOPEditor
          projectId={projectId}
          sop={selectedSOP}
          onSave={async (sop) => {
            await handleSaveSOP(sop);
          }}
          onCancel={() => {
            setShowSOPEditor(false);
            setSelectedSOP(null);
          }}
          isOpen={showSOPEditor}
          isNew={!sops.some(s => s.id === selectedSOP.id)}
        />
      )}

      {/* Add Document Sidebar */}
      <AddDocumentSidebar
        isOpen={showAddDocumentSidebar}
        onClose={() => setShowAddDocumentSidebar(false)}
        projectId={projectId}
        defaultEntityTypes={['SOP']}
        onSuccess={() => {
          // Reload SOPs after document upload
          const loadSOPs = async () => {
            try {
              const col = collection(db, 'projects', projectId, 'sops');
              const snapshot = await getDocs(col);
              const loaded: StandardOperatingProcedure[] = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                  ...data,
                  id: data.id || `SOP-${doc.id.substring(0, 8)}`,
                  _firestoreId: doc.id
                } as StandardOperatingProcedure & { _firestoreId: string };
              });
              setSops(loaded);
            } catch (err) {
              if (process.env.NODE_ENV !== 'production') {
                console.error('Error reloading SOPs:', err);
              }
            }
          };
          loadSOPs();
        }}
      />
    </div>
  );
};

interface SOPEditorProps {
  projectId: string;
  sop: StandardOperatingProcedure;
  onSave: (sop: StandardOperatingProcedure) => Promise<void>;
  onCancel: () => void;
  isOpen: boolean;
  isNew: boolean;
}

const SOPEditor: React.FC<SOPEditorProps> = ({ projectId, sop, onSave, onCancel, isOpen, isNew }) => {
  const { t } = useLocale();
  const [editedSOP, setEditedSOP] = useState<StandardOperatingProcedure>(sop);

  // Handle ESC key to close modal and body scroll lock
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, onCancel]);

  // Update editedSOP when sop prop changes
  useEffect(() => {
    setEditedSOP(sop);
  }, [sop]);

  if (!isOpen) return null;

  return (
    <GlobalSidebar isOpen={isOpen} onClose={onCancel}>
      <div>
        <div className={styles.header}>
          <h2>
            {isNew
              ? (t('project_tab_sop_add') || 'Add SOP')
              : (t('project_tab_sop_edit') || 'Edit SOP')}
            <span className={styles.sopId}> {editedSOP.id}</span>
          </h2>
        </div>

        <form className={styles.form} onSubmit={async (e) => { e.preventDefault(); await onSave(editedSOP); }}>
          <div className={styles.modalTabs}>
            <GlobalTabs
              tabs={[
                {
                  label: t('project_tab_sop_details') || 'Details',
                  content: (
                    <div className={styles.modalTabSection}>
                      <GlobalInput
                        label={t('project_tab_title') || 'Title'}
                        value={editedSOP.title}
                        onChange={(e) => setEditedSOP({ ...editedSOP, title: e.target.value })}
                      />
                      <GlobalInput
                        label={t('project_tab_effective_date') || 'Effective Date'}
                        type="date"
                        value={editedSOP.effective_date}
                        onChange={(e) => setEditedSOP({ ...editedSOP, effective_date: e.target.value })}
                      />
                      <GlobalTextarea
                        label={t('project_tab_description') || 'Description'}
                        value={editedSOP.description}
                        onChange={(e) => setEditedSOP({ ...editedSOP, description: e.target.value })}
                        rows={3}
                      />
                      <GlobalInput
                        label={t('project_tab_department') || 'Department'}
                        value={editedSOP.meta?.department || ''}
                        onChange={(e) => setEditedSOP({ ...editedSOP, meta: { ...(editedSOP.meta || {}), department: e.target.value } })}
                      />
                    </div>
                  )
                },
                {
                  label: t('project_tab_sop_classification') || 'Classification',
                  content: (
                    <div className={styles.modalTabSection}>
                      <GlobalDropdown
                        label={t('project_tab_gxp_relevance') || 'GxP Relevance'}
                        value={editedSOP.gxp_relevance}
                        onChange={(value) => setEditedSOP({ ...editedSOP, gxp_relevance: value as any })}
                        options={[
                          { value: 'None', label: 'None' },
                          { value: 'Low', label: 'Low' },
                          { value: 'Medium', label: 'Medium' },
                          { value: 'High', label: 'High' }
                        ]}
                      />
                      <GlobalDropdown
                        label={t('project_tab_status') || 'Status'}
                        value={editedSOP.status}
                        onChange={(value) => setEditedSOP({ ...editedSOP, status: value as any })}
                        options={[
                          { value: 'Draft', label: t('project_tab_status_draft') || 'Draft' },
                          { value: 'In Review', label: t('project_tab_status_in_review') || 'In Review' },
                          { value: 'Effective', label: t('project_tab_status_approved') || 'Effective' },
                          { value: 'Obsolete', label: t('project_tab_status_rejected') || 'Obsolete' }
                        ]}
                      />
                    </div>
                  )
                },
                {
                  label: t('project_tab_sop_links') || 'Connections',
                  content: (
                    <div className={styles.modalTabSection}>
                      <URSAutoComplete
                        projectId={projectId}
                        label={t('project_tab_linked_urs') || 'Linked URS'}
                        placeholder={t('project_tab_search_requirements') || 'Search URS...'}
                        selectedIds={editedSOP.links.urs_ids}
                        currentEntityId={editedSOP.id}
                        currentEntityType="SOP"
                        onChange={(ids) =>
                          setEditedSOP({
                            ...editedSOP,
                            links: { ...editedSOP.links, urs_ids: ids }
                          })
                        }
                      />
                      <FSAutoComplete
                        projectId={projectId}
                        label={t('project_tab_linked_fs') || 'Linked FS'}
                        placeholder={t('project_tab_search_sops') || 'Search FS...'}
                        selectedIds={editedSOP.links.fs_ids}
                        currentEntityId={editedSOP.id}
                        currentEntityType="SOP"
                        onChange={(ids) =>
                          setEditedSOP({
                            ...editedSOP,
                            links: { ...editedSOP.links, fs_ids: ids }
                          })
                        }
                      />
                      <RiskAutoComplete
                        projectId={projectId}
                        label={t('project_tab_linked_risks') || 'Linked RISK'}
                        placeholder={t('project_tab_search_risks') || 'Search Risks...'}
                        selectedIds={editedSOP.links.risk_ids}
                        currentEntityId={editedSOP.id}
                        currentEntityType="SOP"
                        onChange={(ids) =>
                          setEditedSOP({
                            ...editedSOP,
                            links: { ...editedSOP.links, risk_ids: ids }
                          })
                        }
                      />
                      <TestAutoComplete
                        projectId={projectId}
                        label={t('project_tab_linked_tests') || 'Linked TEST'}
                        placeholder={t('project_tab_search_tests') || 'Search Tests...'}
                        selectedIds={editedSOP.links.test_ids}
                        currentEntityId={editedSOP.id}
                        currentEntityType="SOP"
                        onChange={(ids) =>
                          setEditedSOP({
                            ...editedSOP,
                            links: { ...editedSOP.links, test_ids: ids }
                          })
                        }
                      />
                    </div>
                  )
                }
              ]}
            />
          </div>

          {/* Actions */}
          <div className={styles.actions}>
            <GlobalButton
              type="button"
              onClick={onCancel}
              variant="secondary"
            >
              {t('project_tab_cancel') || 'Cancel'}
            </GlobalButton>
            <GlobalButton
              type="submit"
            >
              {t('project_tab_save') || 'Save'}
            </GlobalButton>
          </div>
        </form>
      </div>
    </GlobalSidebar>
  );
};

